import { useMemo, useState } from "react";
import { z } from "zod";
import { accountService } from "@miplatform/api";
import { CONTACT_DISPLAY_TYPE, HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useEdit } from "@miplatform/hooks";

/**
 * Custom hook for managing contact relationships within the MiContactRelationshipsModal view.
 *
 * @param {object} params - Parameters for the hook.
 * @param {object} params.contact - The contact object associated with the account.
 * @param {string} params.contact.accountId - The ID of the associated account.
 * @param {string} params.contact.id - The unique identifier for the contact.
 * @param {object} params.referenceIds - A mapping of contact levels to their corresponding reference IDs.
 * @param {string} params.defaultContactLevel - The default level assigned to new contact relationships.
 * @param {Function} params.onRefresh - Function to refresh data after creating, updating, or deleting relationships.
 *
 * @returns {object} Hook return values.
 * @returns {boolean} isLoading - Indicates if an operation is currently in progress.
 * @returns {boolean} isDeleteModalOpen - Indicates if the delete confirmation modal is open.
 * @returns {object} contactCategories - Holds contact categories by type, including available options and primary relationship types.
 * @returns {object|null} currentRelationship - The currently selected relationship for deletion or editing.
 * @returns {object} relationshipEdit - State manager for handling relationship form inputs and validations.
 * @returns {Function} getContactCategories - Fetches contact categories based on the specified type.
 * @returns {Function} handleOpenDeleteModal - Opens the delete confirmation modal for a specific relationship.
 * @returns {Function} handleCloseDeleteModal - Closes the delete confirmation modal.
 * @returns {Function} handleAddRelationship - Adds a new contact relationship to the account.
 * @returns {Function} handleUpdateRelationship - Updates an existing contact relationship.
 * @returns {Function} handleDeleteRelationship - Deletes the selected contact relationship after confirmation.
 * @returns {Function} handleResetEdit - Resets the relationship form to its default state.
 */

export const useMiContactRelationshipsModalViewModel = ({
  contact = {},
  referenceIds,
  defaultContactLevel,
  onRefresh,
}) => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const [currentRelationship, setCurrentRelationship] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const contactLevelOptions = useMemo(
    () =>
      Object.keys(referenceIds || {}).map((key) => ({
        label: CONTACT_DISPLAY_TYPE[key],
        value: key,
      })),
    [referenceIds]
  );

  const relationshipEdit = useEdit(
    {
      level: defaultContactLevel,
      label: "",
      isPrimary: false,
    },
    [
      {
        name: "label",
        validationRule: z.string().min(1, "Relationship Type is required"),
      },
    ]
  );

  const handleOpenDeleteModal = (item) => {
    setCurrentRelationship(item);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => setIsDeleteModalOpen(false);

  const handleAddRelationship = async () =>
    stateManager.run(async () => {
      const response = await accountService.contacts.createContactRelationship({
        id: contact.id,
        data: {
          accountId: contact.accountId,
          ...relationshipEdit.getValue(),
          RelatedReferenceId: referenceIds[relationshipEdit.getValue("level")],
        },
        toastMessageConfig: {
          success: {
            text: ` Contact relationship created successfully.`,
          },
          failure: {
            text: `Failed to create contact relationship.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        handleResetEdit();
        onRefresh();
      }
    });

  const handleUpdateRelationship = async ({ id, ...data }) =>
    stateManager.run(async () => {
      const response = await accountService.contacts.updateContactRelationship({
        id: contact.id,
        data: {
          ...data,
          accountId: contact.accountId,
        },
        toastMessageConfig: {
          success: {
            text: ` Contact updated successfully.`,
          },
          failure: {
            text: `Failed to update contact relationship.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
      }
    });

  const handleDeleteRelationship = async () => {
    handleCloseDeleteModal();
    stateManager.run(async () => {
      const response = await accountService.contacts.deleteContactRelationship({
        id: contact.id,
        data: { ...currentRelationship, accountId: contact.accountId },
        toastMessageConfig: {
          success: {
            text: ` Contact relationship deleted successfully.`,
          },
          failure: {
            text: `Failed to delete contact relationship.`,
          },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        onRefresh();
      }
    });
  };

  const handleResetEdit = () =>
    relationshipEdit.update({
      level: defaultContactLevel,
      label: "",
      isPrimary: false,
    });

  return {
    isLoading,
    isDeleteModalOpen,
    currentRelationship,
    contactLevelOptions,
    relationshipEdit,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleAddRelationship,
    handleUpdateRelationship,
    handleDeleteRelationship,
    handleResetEdit,
  };
};
